
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './Statistics.module.scss';
import FileDownloadIcon from '../icons/FileDownloadIcon';
import UploadIcon from '../icons/UploadIcon';
import AddIcon from '../icons/AddIcon';
import CalendarIcon from '../icons/CalendarIcon';
import { UserStatsAPI, apiService } from '../../api/api.service';
import Loader from '../shared/Loader';
import { useNotification } from '../shared/NotificationSystem';
import { fetchStatistics, setPage, setRowsPerPage, setSearchEmail, setStatusFilter, updateUser } from '../../redux/slices/statisticsSlice';
import { RootState } from '../../redux/store';
import { AnyAction } from '@reduxjs/toolkit';
import UserModal from './UserModal';
import { getCookie } from '../../utils/cookieUtils';
import AddUserModal from './AddUserModal';

type AppInstallFilter = 'all' | 'with_app' | 'without_app';

const hasFirstLoginDate = (user: UserStatsAPI) =>
    Boolean(user.first_login_date && String(user.first_login_date).trim());

const Statistics: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showError, showSuccess } = useNotification();

    const { users, isLoading, error, page, rowsPerPage, searchEmail, statusFilter } = useSelector((state: RootState) => state.statistics);
    const [selectedUser, setSelectedUser] = useState<UserStatsAPI | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [appInstallFilter, setAppInstallFilter] = useState<AppInstallFilter>('all');
    const [installDateRange, setInstallDateRange] = useState<[Date | null, Date | null]>([null, null]);

    // Загрузка данных с сервера
    useEffect(() => {
        dispatch(fetchStatistics() as unknown as AnyAction)
            .unwrap()
            .catch((err: Error) => {
                showError('statistics.errorLoading');
                console.error('Error fetching statistics:', err);
            });
    }, [dispatch, showError]);

    const handleChangePage = (newPage: number) => {
        dispatch(setPage(newPage));
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    };

    const handleSearchEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSearchEmail(event.target.value));
    };

    const handleStatusFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        dispatch(setStatusFilter(event.target.value));
    };

    const handleAppInstallFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setAppInstallFilter(event.target.value as AppInstallFilter);
        dispatch(setPage(0));
    };

    const handleInstallDateRangeChange = (dates: [Date | null, Date | null]) => {
        setInstallDateRange(dates);
        dispatch(setPage(0));
    };

    const handleClearInstallDateRange = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setInstallDateRange([null, null]);
        dispatch(setPage(0));
    };

    const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        try {
            const currentLanguage = getCookie('language') || 'ru';
            await apiService.importUsersFromExcel(file, currentLanguage);
            showSuccess('statistics.importSuccess');
            // Перезагружаем данные после импорта
            dispatch(fetchStatistics() as unknown as AnyAction);
        } catch (error) {
            showError('statistics.importError');
            console.error('Import error:', error);
        }

        // Очищаем input
        event.target.value = '';
    };

    const handleUserClick = (user: UserStatsAPI) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    const handleUserUpdate = (updatedUser?: UserStatsAPI) => {
        if (updatedUser) {
            // Обновляем конкретного пользователя в Redux store
            dispatch(updateUser(updatedUser));
        } else {
            // Перезагружаем данные после обновления пользователя
            dispatch(fetchStatistics() as unknown as AnyAction);
        }
    };

    const openAddUserModal = () => setIsAddModalOpen(true);
    const closeAddUserModal = () => setIsAddModalOpen(false);
    const handleUserCreated = () => {
        dispatch(fetchStatistics() as unknown as AnyAction);
    };

    // Функция для локализации статуса подписки
    const getLocalizedStatus = (status: string | null) => {
        if (!status) return '-';
        const statusMap: { [key: string]: string } = {
            'Активна': t('statistics.statusActive'),
            'Отменена': t('statistics.statusCancelled'),
            'Завершена': t('statistics.statusCompleted'),
            'Заменена': t('statistics.statusReplaced'),
            'Заморожена': t('statistics.statusFrozen')
        };
        return statusMap[status] || status;
    };

    // Функция для абсолютного форматирования даты (без учета часовых поясов)
    const formatAbsoluteDate = (dateString: string | null) => {
        if (!dateString) return '-';
        // Извлекаем дату напрямую из ISO строки, игнорируя часовой пояс
        const datePart = dateString.split('T')[0]; // Получаем "2025-09-15"
        const [year, month, day] = datePart.split('-');
        return `${day}.${month}.${year}`;
    };

    const totalUsersCount = users.length;
    const appInstalledUsersCount = users.filter(hasFirstLoginDate).length;
    const [installDateFrom, installDateTo] = installDateRange;
    const hasInstallDateRange = Boolean(installDateFrom || installDateTo);
    const installDateFromValue = installDateFrom ? format(installDateFrom, 'yyyy-MM-dd') : '';
    const installDateToValue = installDateTo ? format(installDateTo, 'yyyy-MM-dd') : '';
    const periodLabel = installDateFrom && installDateTo
        ? `${format(installDateFrom, 'dd.MM.yyyy')} - ${format(installDateTo, 'dd.MM.yyyy')}`
        : installDateFrom
            ? `${format(installDateFrom, 'dd.MM.yyyy')} - ...`
            : t('statistics.installPeriodPlaceholder');

    // Фильтрация данных
    const filteredUsers = users.filter((user: UserStatsAPI) => {
        const matchesEmail = user.email ? user.email.toLowerCase().includes(searchEmail.toLowerCase()) : false;
        const matchesStatus = statusFilter === 'all' || (user.subscription_status && user.subscription_status === statusFilter);
        const matchesAppInstall =
            appInstallFilter === 'all'
                ? true
                : appInstallFilter === 'with_app'
                    ? hasFirstLoginDate(user)
                    : !hasFirstLoginDate(user);
        const firstLoginDateValue = hasFirstLoginDate(user) ? String(user.first_login_date).split('T')[0] : null;
        const matchesInstallPeriod =
            (!installDateFromValue || (firstLoginDateValue !== null && firstLoginDateValue >= installDateFromValue))
            && (!installDateToValue || (firstLoginDateValue !== null && firstLoginDateValue <= installDateToValue));

        return matchesEmail && matchesStatus && matchesAppInstall && matchesInstallPeriod;
    });

    const handleExport = () => {
        const dataForExport = filteredUsers.map((user: UserStatsAPI) => ({
            [t('statistics.userName')]: user.name,
            [t('statistics.email')]: user.email || '-',
            [t('statistics.subscriptionStatus')]: user.subscription_status || '-',
            [t('statistics.subscriptionEndDate')]: formatAbsoluteDate(user.subscription_end_date),
            [t('statistics.lastLogin')]: formatAbsoluteDate(user.last_login_date),
            [t('statistics.firstLogin')]: formatAbsoluteDate(user.first_login_date),
            [t('statistics.registrationDate')]: formatAbsoluteDate(user.registration_date),
            [t('statistics.completedTrainings')]: user.completed_trainings_count,
            [t('statistics.completedTrainingsLast30Days')]: user.completed_trainings_last_30_days,
            [t('statistics.totalTrainings')]: user.completed_trainings_count,
            [t('statistics.language')]: user.language,
        }));

        const ws = XLSX.utils.json_to_sheet(dataForExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, t('statistics.title'));
        XLSX.writeFile(wb, "users-statistics.xlsx");
    };

    // Вычисление пагинации
    const startIndex = rowsPerPage === -1 ? 0 : page * rowsPerPage;
    const endIndex = rowsPerPage === -1 ? filteredUsers.length : Math.min(startIndex + rowsPerPage, filteredUsers.length);
    const displayedData = rowsPerPage === -1 ? filteredUsers : filteredUsers.slice(startIndex, endIndex);
    const totalPages = rowsPerPage === -1 ? 1 : Math.ceil(filteredUsers.length / rowsPerPage);

    if (isLoading) {
        return (
            <div className={styles.loadingOverlay}>
                <Loader fullScreen={false} />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorMessage}>
                {error}
            </div>
        );
    }

    return (
        <div className={styles.statisticsContainer}>
            <div className={styles.header}>
                <h1 className={styles.title}>{t('statistics.title')}</h1>
                <div className={styles.headerActions}>
                    <button
                        className={styles.addUserButton}
                        onClick={openAddUserModal}
                    >
                        <AddIcon />
                        {t('statistics.addUser.button')}
                    </button>
                    <button
                        className={styles.exportButton}
                        onClick={handleExport}
                        disabled={filteredUsers.length === 0}
                    >
                        <UploadIcon />
                        <span className={styles.desktopButtonLabel}>{t('statistics.exportToExcel')}</span>
                    </button>
                    <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleImportFile}
                        id="import-file"
                        style={{ display: 'none' }}
                    />
                    <label htmlFor="import-file" className={styles.importButton}>
                        <FileDownloadIcon />
                        <span className={styles.desktopButtonLabel}>{t('statistics.import')}</span>
                    </label>
                </div>
            </div>

            {/* Поиск и фильтры */}
            <div className={styles.filtersContainer}>
                <div className={styles.searchContainer}>
                    <input
                        type="text"
                        placeholder={t('statistics.searchEmailPlaceholder')}
                        value={searchEmail}
                        onChange={handleSearchEmailChange}
                        className={styles.searchInput}
                    />
                </div>
                <div className={styles.filtersSelectsRow}>
                    <div className={`${styles.filterContainer} ${styles.platformFilterContainer}`}>
                        <select
                            value={appInstallFilter}
                            onChange={handleAppInstallFilterChange}
                            className={styles.statusFilter}
                        >
                            <option value="all">{t('statistics.platformFilterAll')}</option>
                            <option value="with_app">{t('statistics.platformFilterDownloaded')}</option>
                            <option value="without_app">{t('statistics.platformFilterNotDownloaded')}</option>
                        </select>
                    </div>
                    <div className={`${styles.filterContainer} ${styles.periodFilterContainer}`}>
                        <div className={styles.periodDatePicker}>
                            <DatePicker
                                startDate={installDateFrom}
                                endDate={installDateTo}
                                onChange={(dates) => handleInstallDateRangeChange(dates as [Date | null, Date | null])}
                                selectsRange
                                dateFormat="dd.MM.yyyy"
                                customInput={(
                                    <button type="button" className={styles.periodSingleFilter}>
                                        <span className={styles.periodValue}>{periodLabel}</span>
                                        {hasInstallDateRange ? (
                                            <span
                                                className={styles.periodClearIcon}
                                                onClick={handleClearInstallDateRange}
                                                role="button"
                                                aria-label={t('statistics.clearPeriod')}
                                            >
                                                x
                                            </span>
                                        ) : (
                                            <CalendarIcon className={styles.periodIcon} />
                                        )}
                                    </button>
                                )}
                            />
                        </div>
                    </div>
                    <div className={`${styles.filterContainer} ${styles.statusFilterContainer}`}>
                        <select
                            value={statusFilter}
                            onChange={handleStatusFilterChange}
                            className={styles.statusFilter}
                        >
                            <option value="all">{t('statistics.allStatuses')}</option>
                            <option value="Активна">{t('statistics.statusActive')}</option>
                            <option value="Отменена">{t('statistics.statusCancelled')}</option>
                            <option value="Завершена">{t('statistics.statusCompleted')}</option>
                            <option value="Заменена">{t('statistics.statusReplaced')}</option>
                            <option value="Заморожена">{t('statistics.statusFrozen')}</option>
                        </select>
                    </div>
                </div>
            </div>

            <div className={styles.statsSummary}>
                <div className={styles.statsSummaryItem}>
                    <span className={styles.statsSummaryLabel}>{t('statistics.totalUsersLabel')}</span>
                    <span className={styles.statsSummaryValue}>{totalUsersCount}</span>
                </div>
                <div className={styles.statsSummaryItem}>
                    <span className={styles.statsSummaryLabel}>{t('statistics.appInstalledLabel')}</span>
                    <span className={styles.statsSummaryValue}>{appInstalledUsersCount}</span>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>{t('statistics.userName')}</th>
                            <th>{t('statistics.email')}</th>
                            <th>{t('statistics.subscriptionStatus')}</th>
                            <th>{t('statistics.firstLogin')}</th>
                            <th>{t('statistics.lastLogin')}</th>
                            <th className={styles.numericCell}>{t('statistics.completedTrainingsLast30Days')}</th>
                            <th className={styles.numericCell}>{t('statistics.totalTrainings')}</th>
                            <th>{t('statistics.registrationDate')}</th>
                            <th>{t('statistics.subscriptionEndDate')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayedData.map((user: UserStatsAPI, index: number) => {
                            // Извлекаем даты напрямую из ISO строк для корректного сравнения
                            const lastLoginDate = user.last_login_date ? new Date(user.last_login_date.split('T')[0]) : null;
                            const registrationDate = user.registration_date ? new Date(user.registration_date.split('T')[0]) : null;
                            const shouldShowLastLogin = lastLoginDate && registrationDate && lastLoginDate >= registrationDate;

                            return (
                                <tr
                                    key={`${user.email || 'no-email'}-${index}`}
                                    onClick={() => handleUserClick(user)}
                                    className={styles.clickableRow}
                                >
                                    <td>{user.name}</td>
                                    <td>{user.email || '-'}</td>
                                    <td>
                                        <span className={`${styles.statusBadge} ${styles[`status-${user.subscription_status?.toLowerCase() || 'unknown'}`]}`}>
                                            {getLocalizedStatus(user.subscription_status)}
                                        </span>
                                    </td>
                                    <td>
                                        {formatAbsoluteDate(user.first_login_date)}
                                    </td>
                                    <td>
                                        {shouldShowLastLogin ? formatAbsoluteDate(user.last_login_date) : '-'}
                                    </td>
                                    <td className={styles.numericCell}>
                                        {user.completed_trainings_last_30_days}
                                    </td>
                                    <td className={styles.numericCell}>
                                        {user.completed_trainings_count}
                                    </td>
                                    <td>
                                        {formatAbsoluteDate(user.registration_date)}
                                    </td>
                                    <td>
                                        {(() => {
                                            if (!user.subscription_end_date || !user.registration_date) return '-';
                                            const subscriptionEndDate = new Date(user.subscription_end_date.split('T')[0]);
                                            const registrationDate = new Date(user.registration_date.split('T')[0]);
                                            return subscriptionEndDate >= registrationDate
                                                ? formatAbsoluteDate(user.subscription_end_date)
                                                : '-';
                                        })()}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className={styles.paginationContainer}>
                <div className={styles.paginationControls}>
                    <div className={styles.rowsPerPageSelector}>
                        <span>{t('statistics.rowsPerPage')}</span>
                        <select
                            value={rowsPerPage}
                            onChange={handleChangeRowsPerPage}
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={-1}>{t('statistics.allRows')}</option>
                        </select>
                    </div>
                    <div className={styles.paginationText}>
                        {t('statistics.paginationDisplayText', {
                            from: startIndex + 1,
                            to: endIndex,
                            count: filteredUsers.length
                        })}
                    </div>
                    {rowsPerPage !== -1 && (
                        <div className={styles.pageControls}>
                            <button
                                onClick={() => handleChangePage(page - 1)}
                                disabled={page === 0}
                            >
                                &lt;
                            </button>
                            <button
                                onClick={() => handleChangePage(page + 1)}
                                disabled={page >= totalPages - 1}
                            >
                                &gt;
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <UserModal
                user={selectedUser}
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onUserUpdate={handleUserUpdate}
                getLocalizedStatus={getLocalizedStatus}
            />
            <AddUserModal
                isOpen={isAddModalOpen}
                onClose={closeAddUserModal}
                onCreated={handleUserCreated}
                currentLanguage={getCookie('language')?.toUpperCase() || 'RU'}
            />
        </div>
    );
};

export default Statistics;